import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Dialog, IconButton } from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import { MOUSE_DOWN_EVENT } from 'constants/events';
import { FormikProvider, useFormik } from 'formik';
import { debounce, get, has, map } from 'lodash';
import { studentGoalsCreate, useRecentStudentGoals, useStudentList } from 'sdk';

import { ReactComponent as ArrownDownIcon } from 'pages/Teachers/Assignments/CreateMath/assets/arrow-down-icon.svg';
import StandardSelector from 'pages/Teachers/Assignments/CreateMath/components/StandardSelector';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import { notifyError, notifySuccess } from 'utils/notifications';
import { buildAddGoalBackendPayload } from 'utils/payload';
import {
  ADD_GOAL_INITIAL_VALUES,
  getAddGoalValidationSchema
} from 'utils/schemas';

import DateField from 'components/DateField';
import UserContext from 'components/UserContext';

import styles from './styles.module.scss';

const AddGoalDialog = ({
  isOpen,
  onClose,
  onAdd,
  studentId = null,
  isFromStudentList = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStandards, setSelectedStandards] = useState([]);
  const { user } = useContext(UserContext);

  const [selectedStudents, setSelectedStudents] = useState([]);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const studentDropdownRef = useRef(null);

  const accuracyOptions = useMemo(() => {
    return Array.from({ length: 10 }, (_, i) => `${(i + 1) * 10}`);
  }, []);

  const debounceSearch = useMemo(
    () => debounce((value) => setDebouncedSearch(value), 300),
    []
  );

  const { data: studentData, loading: isStudentsLoading } = useStudentList({
    search: debouncedSearch
  });

  const { data: recentGoals, refetch: refetchRecentGoals } =
    useRecentStudentGoals();

  const filteredStudents = studentData || [];

  const validationSchema = useMemo(
    () => getAddGoalValidationSchema({ isFromStudentList }),
    [isFromStudentList]
  );

  const formik = useFormik({
    initialValues: ADD_GOAL_INITIAL_VALUES,
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const backendPayload = buildAddGoalBackendPayload({
          values,
          studentId,
          selectedStandards,
          isFromStudentList
        });

        const { success } = await studentGoalsCreate({ data: backendPayload });

        if (success) {
          trackMixpanelEvent(user.id, 'Teacher creates student goal', {
            feature: 'students',
            view: isFromStudentList ? 'list' : 'goals',
            form: 'add',
            standards: map(selectedStandards, 'code'),
            target_accuracy: values.accuracy
          });
          const successMessage =
            values.studentIds.length > 1
              ? 'Student Goals created successfully!'
              : 'Student Goal created successfully!';
          notifySuccess(successMessage);
          refetchRecentGoals();

          formik.resetForm();
          setSelectedStudents([]);
          setSelectedStandards([]);

          onAdd();
        }
      } catch (error) {
        notifyError('Failed to create student goal. Please try again.');
      } finally {
        setSubmitting(false);
      }
    }
  });

  const { errors, touched, setFieldValue, values } = formik;

  useEffect(() => {
    setFieldValue('standards', map(selectedStandards, 'id'));
  }, [selectedStandards, setFieldValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        studentDropdownRef.current &&
        !studentDropdownRef.current.contains(event.target)
      ) {
        setIsDropdownVisible(false);
      }
    };

    if (isDropdownVisible) {
      document.addEventListener(MOUSE_DOWN_EVENT, handleClickOutside);
    }
    return () =>
      document.removeEventListener(MOUSE_DOWN_EVENT, handleClickOutside);
  }, [isDropdownVisible]);

  const handleSelectStudent = ({ student }) => {
    if (!selectedStudents.find((s) => s.id === student.id)) {
      const updatedStudents = [...selectedStudents, student];
      setSelectedStudents(updatedStudents);
      setFieldValue('studentIds', map(updatedStudents, 'id'));
    }
    setSearchTerm('');
    setDebouncedSearch('');
    setIsDropdownVisible(false);
  };

  const handleSelectRecentGoal = ({ goal }) => {
    if (goal) {
      setFieldValue('goalName', goal.name || '');
      setFieldValue('description', goal.description || '');
    }
  };

  const handleRemoveStudent = ({ studentId }) => {
    const updatedStudents = selectedStudents.filter((s) => s.id !== studentId);
    setSelectedStudents(updatedStudents);
    setFieldValue('studentIds', map(updatedStudents, 'id'));
  };

  const handleDialogClose = () => {
    formik.resetForm();
    setSearchTerm('');
    setSelectedStudents([]);
    setSelectedStandards([]);
    setIsDropdownVisible(false);
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleDialogClose}
      classes={{ paper: styles.dialogPaper }}
      maxWidth="xs"
      fullWidth
    >
      {/* Header */}
      <div className={styles.dialogHeader}>
        <h2 className={styles.dialogTitle}>Add Goal</h2>
        <div className={styles.headerRightGroup}>
          <IconButton
            onClick={handleDialogClose}
            className={styles.closeButton}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </div>
      </div>

      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit} className={styles.formContainer}>
          {isFromStudentList && (
            <div ref={studentDropdownRef} className={styles.studentsContainer}>
              <div className={styles.fieldContainer}>
                <div className={styles.inputGroupWrapper}>
                  <input
                    type="text"
                    placeholder="Search and add students..."
                    className={styles.nativeInput}
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      debounceSearch(e.target.value);
                      setIsDropdownVisible(true);
                    }}
                    onFocus={() => setIsDropdownVisible(true)}
                  />
                  <SearchIcon className={styles.inputInnerIcon} />
                </div>

                {isDropdownVisible && (
                  <div className={styles.dropdownMenu}>
                    {isStudentsLoading ? (
                      <div className={styles.noStudentsFound}>
                        Loading students...
                      </div>
                    ) : filteredStudents.length > 0 ? (
                      filteredStudents.map((student) => (
                        <div
                          key={student.id}
                          onClick={() =>
                            handleSelectStudent({ student: student })
                          }
                          className={styles.dropdownItem}
                        >
                          {student.name}
                        </div>
                      ))
                    ) : (
                      <div className={styles.noStudentsFound}>
                        {debouncedSearch
                          ? 'No students found'
                          : 'Type a name to search...'}
                      </div>
                    )}
                  </div>
                )}

                {selectedStudents.length > 0 && (
                  <div className={styles.studentsListContainer}>
                    <div className={styles.chipsContainer}>
                      {selectedStudents.map((student) => (
                        <div key={student.id} className={styles.studentChip}>
                          <span className={styles.chipText}>
                            {student.name}
                          </span>
                          <button
                            type="button"
                            className={styles.removeBtn}
                            onClick={() =>
                              handleRemoveStudent({ studentId: student.id })
                            }
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {touched.studentIds && errors.studentIds && (
                  <span className={styles.errorText}>{errors.studentIds}</span>
                )}
              </div>
            </div>
          )}

          {/* Goal Name Field Container */}
          <div className={styles.fieldContainer}>
            <div className={styles.inputGroupWrapper}>
              <input
                type="text"
                name="goalName"
                placeholder="Goal Name..."
                className={styles.nativeInput}
                value={values.goalName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <SearchIcon className={styles.inputInnerIcon} />
            </div>
            {touched.goalName && errors.goalName && (
              <span className={styles.errorText}>{errors.goalName}</span>
            )}
          </div>

          {/* Description Field Container */}
          <div className={styles.fieldContainer}>
            <textarea
              name="description"
              placeholder="Description?"
              rows={4}
              className={styles.nativeTextarea}
              value={values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </div>

          {/* Standard Select Field Container */}
          <StandardSelector
            selectedStandards={selectedStandards}
            setSelectedStandards={setSelectedStandards}
            value={values.standards}
            showLabel={false}
            placeholder={'Select a standard...'}
            onChange={(ids) => formik.setFieldValue('standards', ids)}
            error={touched.standards && errors.standards}
          />

          {/* Row Group: Date & Accuracy */}
          <div className={styles.formRowGroup}>
            <div className={styles.rowColumn}>
              <div className={styles.fieldContainer}>
                <DateField
                  name="targetDate"
                  disablePast={true}
                  placeholder="By Date:"
                  onChange={(selectedDate) =>
                    setFieldValue('targetDate', selectedDate)
                  }
                  value={values.targetDate}
                  error={
                    has(errors, 'targetDate') && touched.targetDate
                      ? get(errors, 'targetDate')
                      : null
                  }
                  helperText={
                    touched.targetDate && errors.targetDate
                      ? get(errors, 'targetDate')
                      : null
                  }
                />
              </div>
            </div>

            <div className={styles.rowColumn}>
              <div className={styles.fieldContainer}>
                <div
                  className={`${styles.selectGroupWrapper} ${styles.rowFieldElement}`}
                >
                  <select
                    name="accuracy"
                    className={styles.nativeSelect}
                    value={values.accuracy}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    {accuracyOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        Target Accuracy: {opt}%
                      </option>
                    ))}
                  </select>
                  <ArrownDownIcon className={styles.chevron} />
                </div>
              </div>
            </div>
          </div>

          <div className={styles.recentGoalsWrapper}>
            <h4 className={styles.recentGoalsTitle}>Recent Goals</h4>
            <div className={styles.recentItemsContainer}>
              {recentGoals && recentGoals.length >= 1 ? (
                recentGoals.map((recentGoal, index) => (
                  <button
                    key={index}
                    type="button"
                    className={styles.recentGoalBadge}
                    onClick={() => {
                      handleSelectRecentGoal({ goal: recentGoal });
                    }}
                  >
                    {recentGoal.name}
                  </button>
                ))
              ) : (
                <span className={styles.noGoalsLabel}>
                  No recent goals found.
                </span>
              )}
            </div>
          </div>

          <button type="submit" className={styles.submitActionButton}>
            <span></span>
            <span>ADD GOAL</span>
            <ArrowForwardIcon className={styles.submitButtonIcon} />
          </button>
        </form>
      </FormikProvider>
    </Dialog>
  );
};

export default AddGoalDialog;
